import { pdf } from '@react-pdf/renderer';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { IFreeResource } from 'types/free-resource';

import {
  Card,
  CardContent,
  CardMedia,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import Button from 'creative-components/CustomButtons/Button';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import DashboardBubble from 'components/DashboardBubble/DashboardBubble';
import DashboardSection from 'components/DashboardSection/DashboardSection';
import SevenMistakesDocument from 'components/FreeResources/SevenMistakesDocument';
import SevenSignsDocument from 'components/FreeResources/SevenSignsDocument';
import SmartSellersDocument from 'components/FreeResources/SmartSellersDocument';
import UltimateCityGuideDocument from 'components/FreeResources/UltimateCityGuideDocument';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import analyticsClient from 'utils/analytics';
import { isMarketingProfileCompleted } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  introSection: {
    maxWidth: 1100,
    marginBottom: theme.spacing(4),
  },
  introBody: {
    color: theme.palette.text.secondary,
  },
  title: {
    margin: '15px 0 12px',
    fontWeight: 'bold',
  },
  subTitle: {
    fontSize: '14px',
    color: theme.palette.gray.main,
    fontWeight: 500,
    lineHeight: '20px',
  },
  warningText: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    color: theme.palette.warning.main,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  warningLink: {
    color: theme.palette.warning.dark || theme.palette.warning.main,
    textDecoration: 'underline',
  },
  profileStatusBanner: {
    margin: '24px 0',
  },
  cardsGrid: {
    marginTop: theme.spacing(2),
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 18,
    boxShadow: 'rgba(0, 0, 0, 0.1) 0rem 0.25rem 0.375rem -0.0625rem, rgba(0, 0, 0, 0.06) 0rem 0.125rem 0.25rem -0.0625rem',
    overflow: 'visible',
  },
  cardMedia: {
    height: 200,
    margin: theme.spacing(-3, 0, 0),
    borderRadius: 16,
    boxShadow: '0 4px 10px rgba(23, 43, 77, 0.48)',
    backgroundSize: 'cover',
    backgroundPosition: 'center 15%',
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardType: {
    marginTop: theme.spacing(1),
    color: theme.palette.gray.main,
    fontWeight: 500,
    letterSpacing: 0.6,
  },
  cardName: {
    marginTop: theme.spacing(1),
    color: theme.palette.darkBlueGray.main,
    fontWeight: 700,
    fontSize: '18px',
    letterSpacing: 0.6,
  },
  cardDescription: {
    marginTop: theme.spacing(2),
    color: theme.palette.lightGray.main,
    fontWeight: 500,
    letterSpacing: 0.6,
  },
  cardButton: {

  },
}));

const FREE_RESOURCES: IFreeResource[] = [
  {
    id: 'seven-mistakes-home-sellers-make',
    name: '7 Mistakes Home Sellers Make',
    type: 'Flyer - 8.5" x 11"',
    description: 'An educational flyer that highlights the most common pitfalls sellers face—position yourself as the expert who helps clients avoid costly mistakes.',
    imageUrl: '/free-resources/seven-mistakes-home-sellers-make/cover.png',
    DocumentComponent: SevenMistakesDocument,
  },
  {
    id: 'ultimate-city-activity-guide',
    name: 'Ultimate City Activity Guide',
    type: 'Flyer - 10.7" x 8"',
    description: 'Position yourself as the local expert with a beautifully designed guide of top attractions, dining, and activities in your city—perfect for welcoming clients and prospects.',
    imageUrl: '/free-resources/ultimate-city-activity-guide/cover.png',
    DocumentComponent: UltimateCityGuideDocument,
  },
  {
    id: 'the-smart-sellers-playbook',
    name: 'The Smart Seller’s Playbook',
    type: 'Flyer - 8.5" x 11"',
    description: 'Educate homeowners with a practical, step-by-step guide that prepares them to sell faster and smarter while positioning you as the trusted advisor.',
    imageUrl: '/free-resources/the-smart-sellers-playbook/cover.png',
    DocumentComponent: SmartSellersDocument,
  },
  {
    id: 'seven-signs-its-time',
    name: '7 Signs It’s Time to Right-Size Your Home',
    type: 'Flyer - 20" x 11.25"',
    description: 'Engage potential sellers with a helpful flyer that outlines the most common signs it’s time to move into a home that better fits their lifestyle.',
    imageUrl: '/free-resources/seven-signs-its-time/cover.png',
    DocumentComponent: SevenSignsDocument,
  },
];

const FreeResourcesPage = () => {
  const classes = useStyles();
  const { userInfo } = useUserInfoContext() as any;
  const { user } = useAuthDataContext();
  const [generatingId, setGeneratingId] = React.useState<string | null>(null);

  if (!userInfo) {
    return (<LoadingIndicator />);
  }

  const marketingProfileComplete = isMarketingProfileCompleted(userInfo?.agentInfo);

  const handleGenerate = async (resource: IFreeResource) => {
    const { id, name, DocumentComponent } = resource;

    if (!marketingProfileComplete) {
      toast.warning((
        <div style={{ fontWeight: 600 }}>
          You must complete your marketing profile before you can download these free resources!
          <br />
          <Link to="/dashboard/marketing-profile" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Go to Marketing Profile
          </Link>
        </div>
      ));
      return;
    }

    try {
      setGeneratingId(id);

      const blob = await pdf(<DocumentComponent name={name} userInfo={userInfo} />).toBlob();
      const fileUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.download = `${name}.pdf`; // Set the filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(fileUrl), 30_000);

      // Track GTM event
      analyticsClient.track('free_resource_downloaded', {
        agentdata_user_id: user._id,
        email: user.email,
        resource_id: id,
      });
    } catch (error) {
      console.error('Failed to generate PDF', error);
      toast.error('We were unable to generate that PDF. Please try again.');
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div className={classes.root}>
      <DashboardSection sectionName="Free Resources" />

      {/* TODO: TEMP FOR TESTING */}
      {/* <PDFViewer style={{ width: '100%', height: '90vh' }}>
        <UltimateCityGuideDocument userInfo={userInfo} />
      </PDFViewer> */}

      <div className={classes.introSection}>
        <h4 className={classes.title}>
          Ready-to-go marketing designed for you
        </h4>
        <h5 className={classes.subTitle}>
          Discover professionally designed marketing materials that Harvist keeps fresh and on-brand for you. Your marketing profile details are automatically pulled in, eliminating most of the manual design work. For resources with unique local details—like a city name or list of nearby places—you can add the finishing touches to make them truly yours. We give you a strong head start, and you just add the final touch.
        </h5>

        {!marketingProfileComplete && (
          <DashboardBubble
            variant="status"
            tone="warning"
            rootClass={classes.profileStatusBanner}
            icon={<WarningRoundedIcon />}
            title="Complete your marketing profile"
            content="Finish your marketing profile before downloading personalized free resources."
            action={(
              <Link to="/dashboard/marketing-profile">
                Complete profile
              </Link>
            )}
          />
        )}
      </div>

      <Grid container spacing={8} className={classes.cardsGrid}>
        {FREE_RESOURCES.map((resource) => (
          <Grid item xs={12} sm={6} md={4} key={resource.id} style={{ minWidth: '400px', maxWidth: '500px' }}>
            <Card className={classes.card}>
              <CardMedia
                className={classes.cardMedia}
                image={resource.imageUrl}
                title={resource.name}
              />
              <CardContent className={classes.cardContent}>
                <div>
                  <div className={classes.cardType}>
                    {resource.type}
                  </div>
                  <div className={classes.cardName}>
                    {resource.name}
                  </div>
                  <div className={classes.cardDescription}>
                    {resource.description}
                  </div>
                </div>
                <div className={classes.cardButton}>
                  <Button
                    round
                    color="orange"
                    disabled={generatingId === resource.id}
                    onClick={() => handleGenerate(resource)}
                  >
                    {generatingId === resource.id ? 'Generating…' : 'Generate'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default FreeResourcesPage;
